import { makeAutoObservable, runInAction } from 'mobx'
import axios, { type CancelTokenSource } from 'axios'
import { type ReactNode } from 'react'
import { type IDropdownItem } from 'shared/ui'
import { logger } from 'shared/lib'
import {
  ChatbotApi,
  ChatbotListParams,
  ChatbotStatus,
  type IChatbotFullData,
  type IParamsGetChatbotList,
} from 'entities/Chatbot'

type IAiRepliesStoreProps = {
  clearError: () => void
  disabled?: boolean
  disabledReason?: string | null
}

export class AiRepliesStore {
  clearError
  disabled = false
  disabledReason: string | null = null

  constructor({ clearError, disabled, disabledReason }: IAiRepliesStoreProps) {
    this.clearError = clearError
    this.disabled = disabled ?? false
    this.disabledReason = disabledReason ?? null
    makeAutoObservable(this)
  }

  page = 1
  last_page = 1
  limit = 20
  search = ''
  loading = false
  loadingMore = false

  isAiRepliesActive = false
  chatbot_id: null | number = null
  agentItem: IDropdownItem | null = null
  private _dropDownItems: IDropdownItem[] = []

  cancelTokenSource: CancelTokenSource | null = null
  error: ReactNode = null

  get dropDownItems(): IDropdownItem[] {
    return this._dropDownItems
  }

  private initCancelTokenSource = () => {
    if (this.cancelTokenSource) this.cancelTokenSource.cancel()

    this.cancelTokenSource = axios.CancelToken.source()
  }

  setError = (error: ReactNode) => {
    this.error = error
  }

  setDisabled = (disabled: boolean, disabledReason: string | null) => {
    this.disabled = disabled
    this.disabledReason = disabledReason
  }

  loadData = async () => {
    try {
      this.initCancelTokenSource()

      runInAction(() => {
        if (this.page === 1) {
          this.loading = true
          this._dropDownItems = []
        } else {
          this.loadingMore = true
        }
      })

      const { data } = await ChatbotApi.getChatbotList(this.params, {
        ...(this.cancelTokenSource ? { cancelToken: this.cancelTokenSource.token } : null),
      })

      if (data) {
        runInAction(() => {
          if (this.page === 1) {
            this._dropDownItems = data.data.map(this.agentToDropdownItem)
          } else {
            this._dropDownItems = [
              ...this.dropDownItems,
              ...data.data.map(this.agentToDropdownItem),
            ]
          }

          this.last_page = data.meta.last_page
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this.loading = false
        this.loadingMore = false
      })
    }
  }

  loadDataByChatbotId = async (id: number) => {
    try {
      const { data } = await ChatbotApi.getChatbot(id)

      if (data) this.setAiItem(id, this.agentToDropdownItem(data))
    } catch (e) {
      logger.error(e)
    }
  }

  get params(): IParamsGetChatbotList {
    return {
      page: this.page,
      limit: this.limit,
      [ChatbotListParams.Name]: this.search,
      [ChatbotListParams.Status]: ChatbotStatus.Active,
    }
  }

  private agentToDropdownItem = (agent: IChatbotFullData): IDropdownItem => {
    return {
      id: agent.id,
      height: 32,
      data: agent,
      label: agent.name,
      disabled: !agent.is_active,
      noRenderEmptyActiveValue: true,
    }
  }

  setAiRepliesState = (value: boolean) => {
    this.isAiRepliesActive = value

    if (!value) {
      this.chatbot_id = null
      this.agentItem = null
    }
  }
  setAiItem = (id: number, item: IDropdownItem) => {
    this.chatbot_id = id
    this.agentItem = item
  }

  onChangeOpen = (value: boolean) => {
    if (!value) {
      this.setSearch('')
    }

    this.clearError()
    if (value && !this._dropDownItems.length) {
      void this.loadData()
    }
  }

  setSearch = (value: string) => {
    this.page = 1
    this.last_page = 1
    this.search = value
  }

  onLoadMore = () => {
    if (this.last_page <= this.page) return
    if (this.loadingMore) return

    this.page += 1
    void this.loadData()
  }
}
